import useSWR, { unstable_serialize } from 'swr';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { transactionsDepartment } from 'integration/generated/transactions';
import { getContractIdParamFromContext, getSettings, useSettings } from '@/data/Settings';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { GetServerSidePropsContext } from 'next';
import { Cache } from '@/data/types/Cache';
import { getUser } from '@/data/User';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { shrink } from '@/data/utils/keyUtil';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';

type FindDepartmentQuery = {
	catentries?: string | string[];
};

export const findDepartmentFetcher =
	(pub: boolean) => async (storeId: string, query: FindDepartmentQuery, params: RequestParams) => {
		const response = await transactionsDepartment(pub).findDepartment(storeId, query, params);
		return response;
	};

const DATA_KEY_FIND_DEPARTMENT = 'findDepartment';

export const getFindDepartment = async (
	cache: Cache,
	query: FindDepartmentQuery,
	context: GetServerSidePropsContext
) => {
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { storeId, langId } = getServerSideCommon(settings, context);

	const _query = {
		storeId,
		langId,
		...query,
		...getContractIdParamFromContext(user?.context),
	};

	const key = unstable_serialize([shrink(_query), DATA_KEY_FIND_DEPARTMENT]);
	const params = constructRequestParamsWithPreviewToken({ context });
	const cacheScope = getServerCacheScope(context, user.context);

	const value =
		(cache.get(key, cacheScope) as Promise<
			| {
					departments: Record<string, string>;
			  }
			| undefined
		>) || findDepartmentFetcher(false)(storeId, _query, params);

	cache.set(key, value, cacheScope);

	return (await value) ?? undefined;
};

export const useFindDepartment = (args?: FindDepartmentQuery) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const rawSwr = useSWR(
		storeId && args?.catentries ? [{ storeId, ...args }, DATA_KEY_FIND_DEPARTMENT] : null,
		async ([{ storeId, ...query }]) => findDepartmentFetcher(true)(storeId, query, params)
	);

	return rawSwr;
};
